import React, { useState } from "react"
import Input from "../../../../../utils/Form/Input"
import InputFeedback from "../InputFeedback"
import { Machine } from "xstate"
import { useMachine } from "@xstate/react"
import { FaInfoCircle } from "react-icons/fa"
import CalculatorInputWrapper from "../CalculatorInputWrapper"

interface iCalculatorTextInput {
  label: string
  onChange: (e: any) => void
  placeholder?: string
  value: any
  type?: string
  error?: string | null // Here "error" takes precedence over "warning"
  warning: string | null
  disable: boolean
  unit?: string
  showFeedback?: boolean
  shortInfo: string
  extendedInfo: string
}

const CalculatorTextInput: React.FC<iCalculatorTextInput> = ({
  label,
  onChange,
  placeholder,
  value,
  type,
  disable,
  error,
  warning,
  unit,
  showFeedback,
  shortInfo,
  extendedInfo,
}) => {
  const inputMachine = Machine({
    id: "input-machine",
    initial: "idle",
    states: {
      idle: {
        on: {
          ENABLE: "enabled",
        },
      },
      enabled: {
        // When the state is enabled then input will show the warning and errors etc
        on: {
          IDLE: "idle",
        },
      },
    },
  })
  const [currentInputState, sendInputState] = useMachine(inputMachine)
  const [hideInputLabelUnit, setHideInputLabelUnit] = useState(false)

  let inputBorderColor = ""
  if (currentInputState.matches("enabled")) {
    if (error) {
      inputBorderColor = "border-red-600 hover:border-red-700"
    } else if (warning) {
      inputBorderColor = "border-yellow-500 hover:border-yellow-600"
    }
  }

  const updateHideInputLabelUnitState = () => {
    let state = hideInputLabelUnit
    if (value) {
      if (error || warning) state = false
      else state = true
    } else state = false
    setHideInputLabelUnit(state)
  }

  const getWarningFeedback = () => {
    if (currentInputState.matches("enabled")) return warning
    return null
  }

  const getErrorFeedback = () => {
    if (currentInputState.matches("enabled")) return error
    return null
  }

  const getValueFeedback = () => {
    if (!value || isNaN(value)) return null
    return parseFloat(value).toFixed(1)
  }

  return (
    <CalculatorInputWrapper
      label={label}
      unit={unit}
      extendedInfo={extendedInfo}
      hideUnit={Boolean(value) && hideInputLabelUnit}
    >
      <div className="py-2" />
      <div className="grid sm:grid-cols-2 md:grid-cols-5">
        <div className="sm:col-span-1 md:col-span-3">
          <Input
            type={type}
            value={value}
            onBlur={() => {
              sendInputState("ENABLE")
              updateHideInputLabelUnitState()
            }}
            onChange={(e: any) => {
              if (disable) return
              if (currentInputState.matches("enabled")) sendInputState("IDLE")
              onChange(e)
            }}
            placeholder={placeholder}
            className={[inputBorderColor, "shadow"].join(" ")}
          />

          <p className="mt-4 text-gray-500 text-sm leading-none flex items-center">
            <span className="inline-block mr-1">
              <FaInfoCircle />
            </span>
            <span className="inline-block">{shortInfo}</span>
          </p>
        </div>
        <div className="sm:col-span-1 md:col-span-2 flex justify-end mt-4 sm:mt-0">
          <div>
            <InputFeedback
              title={unit}
              value={getValueFeedback()}
              show={showFeedback}
              warning={getWarningFeedback()}
              error={getErrorFeedback()}
            />
          </div>
        </div>
      </div>
    </CalculatorInputWrapper>
  )
}

export default CalculatorTextInput
